import { useState, useEffect } from "react";
import QRCode from "qrcode";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Image from "@/components/ui/image";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState(""); // For password reset
  const [resetCode, setResetCode] = useState(""); // For password reset
  const [isPasswordResetRequired, setIsPasswordResetRequired] = useState(false); // Track password reset state
  const [isForgotPasswordFlow, setIsForgotPasswordFlow] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [secretCode, setSecretCode] = useState<string | null>(null); // Store the MFA secret code
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null); // Store the QR code URL
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState(""); // For password reset
  const { signIn, verifyMfaCodeForSetup, verifyMfaCodeForLogin, forgotPassword, resetPassword, changePasswordBySession } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await signIn(username, password);

      if (response?.ChallengeName === "MFA_SETUP" && response?.SecretCode) {
        setSecretCode(response.SecretCode);

        const otpAuthUrl = `otpauth://totp/PeakRevenue:${username}?secret=${response.SecretCode}&issuer=PeakRevenue`;
        const qrCode = await QRCode.toDataURL(otpAuthUrl);
        setQrCodeUrl(qrCode);
      } else if (response?.ChallengeName === "SOFTWARE_TOKEN_MFA") {
        setSecretCode(null);
        setMfaRequired(true);
      } else if (response?.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        setIsPasswordResetRequired(true); // Trigger password reset flow
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Invalid username or password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if(newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      setIsLoading(false);
      return;
    }

    try {
      await resetPassword(username, resetCode, newPassword);
      setIsForgotPasswordFlow(false);
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordResetWithoutCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await changePasswordBySession(username, newPassword);
      setIsPasswordResetRequired(false); // Return to login flow
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClick = async () => {
    setError(null);

    if (!username.trim()) {
      setError("Enter your username first to receive a reset code.");
      return;
    }

    setIsLoading(true);
    try {
      await forgotPassword(username.trim());
      setIsForgotPasswordFlow(true);
      setMfaRequired(false);
      setSecretCode(null);
    } catch (error) {
      console.error("Forgot password error:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  const handleMfaSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await verifyMfaCodeForSetup(username, mfaCode);
    } catch (error) {
      console.error("MFA verification error:", error);
      setError("Invalid MFA code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await verifyMfaCodeForLogin(username, mfaCode);
    } catch (error) {
      console.error("MFA verification error:", error);
      setError("Invalid MFA code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container min-h-screen flex items-center justify-center bg-background p-4">
      <div className="auth-form-container w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <Image
            src="/lovable-uploads/84af94cd-12fe-494a-85ed-aed9f5638a4d.png"
            alt="PeakRevenue Logo"
            className="h-16 w-16 mx-auto mb-2"
            objectFit="contain"
          />
          <h1 className="text-2xl font-bold text-primary">PeakRevenue</h1>
        </div>

        <Card className="border shadow-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Sign in</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials below to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {secretCode ? (
              <div className="space-y-4">
                <p className="text-sm">
                  Use the following QR code to set up your authenticator app:
                </p>
                {qrCodeUrl && (
                  <div className="flex justify-center">
                    <img src={qrCodeUrl} alt="MFA QR Code" className="w-40 h-40" />
                  </div>
                )}
                <p className="text-sm">
                  If you cannot scan the QR code, use this secret key:
                </p>
                <div className="p-4 bg-gray-100 rounded-md text-center break-words">
                  <code className="text-sm font-mono">{secretCode}</code>
                </div>
                <p className="text-sm">
                  After setting up your authenticator app, enter the code it generates below:
                </p>
                <form onSubmit={handleMfaSetup} className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="mfa-code">Enter the code from your authenticator app</Label>
                    <Input
                      id="mfa-code"
                      type="text"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      required
                      autoComplete="one-time-code"
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </form>
              </div>
            ) : mfaRequired ? (
              <form onSubmit={handleMfaSubmit} className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="mfa-code">Authenticator Code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    disabled={isLoading}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>
              </form>
            ) : isPasswordResetRequired ? (
              <form onSubmit={handlePasswordResetWithoutCode} className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    autoComplete="confirm-new-password"
                    disabled={isLoading}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            ) : isForgotPasswordFlow ? (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="reset-code">Reset Code</Label>
                  <Input
                    id="reset-code"
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    disabled={isLoading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    autoComplete="confirm-new-password"
                    disabled={isLoading}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="w-full"
                  disabled={isLoading}
                  onClick={() => {
                    setIsForgotPasswordFlow(false);
                    setResetCode("");
                    setNewPassword("");
                    setConfirmNewPassword("");
                    setError(null);
                  }}
                >
                  Back to sign in
                </Button>
              </form>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    className="w-full"
                    disabled={isLoading}
                    onClick={handleForgotPasswordClick}
                  >
                    Forgot password?
                  </Button>
                </form>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account? Contact your administrator.
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Login;
